import { FileExportFormat } from '@epicuro-next/ataraxia/models';
import {
  CBasketConfigurationKeys,
  IBasketAction,
} from '@epicuro-next/data/baskets';
import { Filter } from '@epicuro-next/models/filters';
import { IConfigurations } from '@epicuro-next/platform/configuration';
import {
  BasketRowsGroupType,
  OrderType,
} from 'graphql-api/baskets-api-interfaces';

import { BasketHeaderInfoModel, SortParamsTuple } from './models';
import { BasketColumnsConfiguration } from './models/basket-columns-configuration';
import { ManualDiscountsRules } from './types';

export interface CBasketConfigurations
  extends IConfigurations<CBasketConfigurationKeys> {
  [CBasketConfigurationKeys.Actions]: IBasketAction[];
  [CBasketConfigurationKeys.AddressLineTitleProperties]: string[];
  [CBasketConfigurationKeys.AllowedMixedBasket]: boolean;
  [CBasketConfigurationKeys.AllowResendOrderConfirmation]: boolean;
  [CBasketConfigurationKeys.AlternativeAddressTitleType]: boolean;
  [CBasketConfigurationKeys.AuxGroupingFn]: string;
  [CBasketConfigurationKeys.BasketColumns]: string;
  [CBasketConfigurationKeys.BasketDownloadFormat]: FileExportFormat;
  [CBasketConfigurationKeys.BasketPolling]: boolean;
  [CBasketConfigurationKeys.BasketUploadSkuLimit]: number;
  [CBasketConfigurationKeys.CheckoutAdjustMessageDelay]: number;
  [CBasketConfigurationKeys.CopyNowBasketEnabled]: boolean;
  [CBasketConfigurationKeys.CopyProductsAllowedOrderTypes]: OrderType[];
  [CBasketConfigurationKeys.CopyProductsEnabled]: boolean;
  [CBasketConfigurationKeys.DefaultSorting]: Partial<SortParamsTuple>;
  [CBasketConfigurationKeys.EnableBasketDeletion]: boolean;
  [CBasketConfigurationKeys.EnableBasketInsights]: boolean;
  [CBasketConfigurationKeys.EnableBasketName]: boolean;
  [CBasketConfigurationKeys.EnableCopy]: boolean;
  [CBasketConfigurationKeys.EnableReschedule]: boolean;
  [CBasketConfigurationKeys.GroupByOptions]: BasketRowsGroupType[];
  [CBasketConfigurationKeys.GroupedProductTitleShape]: string[];
  [CBasketConfigurationKeys.HeaderLevelCustomOrderReference]: boolean;
  [CBasketConfigurationKeys.HideProductTitleInArticle]: boolean;
  [CBasketConfigurationKeys.HighlightCloseoutProducts]: boolean;
  [CBasketConfigurationKeys.ManualDiscounts]: string;
  [CBasketConfigurationKeys.ManualDiscountAppliedToSelected]: boolean;
  [CBasketConfigurationKeys.MaxDiscountPercentage]: string | number;
  [CBasketConfigurationKeys.ManualDiscountPercentageEntries]: string;
  [CBasketConfigurationKeys.MaxOrderRefLength]: number;
  [CBasketConfigurationKeys.MoveBasketEnabled]: boolean;
  [CBasketConfigurationKeys.OrderSuccessThanksAccountName]: boolean;
  [CBasketConfigurationKeys.ParkingOptionsEnabled]: boolean;
  [CBasketConfigurationKeys.PoNumberRequired]: boolean;
  [CBasketConfigurationKeys.PreselectPropertyBag]: boolean;
  [CBasketConfigurationKeys.PrepackTitleSuffixEnabled]: boolean;
  [CBasketConfigurationKeys.PreviewSizes]: boolean;
  [CBasketConfigurationKeys.PropertyBagDropdowns]: Filter[];
  [CBasketConfigurationKeys.ProductPropsFixModal]: string[];
  [CBasketConfigurationKeys.RemoveAllBackorderItemsEnabled]: boolean;
  [CBasketConfigurationKeys.RemoveErrorItemsEnabled]: boolean;
  [CBasketConfigurationKeys.RestrictMixedBasketDropShip]: boolean;
  [CBasketConfigurationKeys.ReuseDuringEmail]: boolean;
  [CBasketConfigurationKeys.ShippingOptionEnabled]: boolean;
  [CBasketConfigurationKeys.ShippingOptionUsePreferred]: boolean;
  [CBasketConfigurationKeys.ShipInFullEnabled]: boolean;
  [CBasketConfigurationKeys.ShipToInfo]: boolean;
  [CBasketConfigurationKeys.ShowBasketNotes]: boolean;
  [CBasketConfigurationKeys.ShowDeliveryLinePrefixTitle]: boolean;
  [CBasketConfigurationKeys.ShowIdInArticleGroup]: boolean;
  [CBasketConfigurationKeys.ShowPrepackNote]: boolean;
  [CBasketConfigurationKeys.ShowRequestedOnPlacedColumns]: string[];
  [CBasketConfigurationKeys.SortingPerGroup]: string;
  [CBasketConfigurationKeys.UploadMDOBasketEnabled]: boolean;
  [CBasketConfigurationKeys.UploadSDOBasketEnabled]: boolean;
  [CBasketConfigurationKeys.BasketHeaderInfo]: string;
  [CBasketConfigurationKeys.ShowDropShipNote]: boolean;
  [CBasketConfigurationKeys.ShowButtonsRowInBasket]: boolean;
  [CBasketConfigurationKeys.ValidatorBasketFunctions]: string;
  [CBasketConfigurationKeys.CheckEveryGrossPriceForManualDiscount]: boolean;
  [CBasketConfigurationKeys.UserFeedbackEnabled]: boolean;
  [CBasketConfigurationKeys.UserFeedbackWriteEffectEnabled]: boolean;
}

export const basketColumnsNotConfiguredValue: BasketColumnsConfiguration = {
  default: {
    before: [],
    after: [],
  },
};

export const defaultManualDiscountsRules: ManualDiscountsRules = {
  allowed: [],
  validations: {
    allowValueBelowCDiscount: {
      closedOut: false,
      preOrder: false,
      reOrder: false,
    },
  },
};

export const defaultBasketHeaderInfoRules: BasketHeaderInfoModel = {
  showTotalBasketValue: true,
  showPrefix: false,
};

export const basketConfigurationsDefaultValues = {
  [CBasketConfigurationKeys.Actions]: [],
  [CBasketConfigurationKeys.AddressLineTitleProperties]: ['id', 'title'],
  [CBasketConfigurationKeys.AllowedMixedBasket]: true,
  [CBasketConfigurationKeys.AllowResendOrderConfirmation]: false,
  [CBasketConfigurationKeys.AlternativeAddressTitleType]: false,
  [CBasketConfigurationKeys.AuxGroupingFn]: '',
  [CBasketConfigurationKeys.BasketColumns]: JSON.stringify(
    basketColumnsNotConfiguredValue,
  ),
  [CBasketConfigurationKeys.BasketPolling]: false,
  [CBasketConfigurationKeys.BasketUploadSkuLimit]: 10000,
  [CBasketConfigurationKeys.BasketUploadRegardlessPreset]: false,
  [CBasketConfigurationKeys.CheckoutAdjustMessageDelay]: 0,
  [CBasketConfigurationKeys.CopyNowBasketEnabled]: false,
  [CBasketConfigurationKeys.CopyProductsAllowedOrderTypes]: [OrderType.Future],
  [CBasketConfigurationKeys.DefaultSorting]: [] as Partial<SortParamsTuple>,
  [CBasketConfigurationKeys.EnableBasketDeletion]: false,
  [CBasketConfigurationKeys.GroupByOptions]: [
    BasketRowsGroupType.Article,
    BasketRowsGroupType.Category,
    BasketRowsGroupType.Delivery,
  ],
  [CBasketConfigurationKeys.HeaderLevelCustomOrderReference]: true,
  [CBasketConfigurationKeys.HighlightCloseoutProducts]: false,
  [CBasketConfigurationKeys.ManualDiscounts]: JSON.stringify(
    defaultManualDiscountsRules,
  ),
  [CBasketConfigurationKeys.ManualDiscountAppliedToSelected]: false,
  [CBasketConfigurationKeys.MaxDiscountPercentage]: 0,
  [CBasketConfigurationKeys.MaxOrderRefLength]: 20,
  [CBasketConfigurationKeys.OrderSuccessThanksAccountName]: false,
  [CBasketConfigurationKeys.ParkingOptionsEnabled]: false,
  [CBasketConfigurationKeys.PoNumberRequired]: false,
  [CBasketConfigurationKeys.PreselectPropertyBag]: false,
  [CBasketConfigurationKeys.PrepackTitleSuffixEnabled]: false,
  [CBasketConfigurationKeys.PropertyBagDropdowns]: [],
  [CBasketConfigurationKeys.ProductPropsFixModal]: [],
  [CBasketConfigurationKeys.RemoveAllBackorderItemsEnabled]: false,
  [CBasketConfigurationKeys.RemoveErrorItemsEnabled]: false,
  [CBasketConfigurationKeys.RestrictMixedBasketDropShip]: false,
  [CBasketConfigurationKeys.ReuseDuringEmail]: false,
  [CBasketConfigurationKeys.ShippingOptionEnabled]: false,
  [CBasketConfigurationKeys.ShippingOptionUsePreferred]: false,
  [CBasketConfigurationKeys.ShipInFullEnabled]: false,
  [CBasketConfigurationKeys.ShowBasketNotes]: false,
  [CBasketConfigurationKeys.ShowBasketManualDiscountInputForGroupTypes]: [],
  [CBasketConfigurationKeys.ShowDeliveryLinePrefixTitle]: false,
  [CBasketConfigurationKeys.ShowIdInArticleGroup]: false,
  [CBasketConfigurationKeys.ShowRequestedOnPlacedColumns]: [],
  [CBasketConfigurationKeys.SortingPerGroup]: '{}',
  [CBasketConfigurationKeys.UploadMDOBasketEnabled]: false,
  [CBasketConfigurationKeys.UserRolesAllowedToSeeManualDiscountsInNewishBasket]:
    [],
  [CBasketConfigurationKeys.BasketHeaderInfo]: JSON.stringify(
    defaultBasketHeaderInfoRules,
  ),
  [CBasketConfigurationKeys.ShowDropShipNote]: false,
  [CBasketConfigurationKeys.ShowButtonsRowInBasket]: false,
  [CBasketConfigurationKeys.ValidatorBasketFunctions]: '[]',
  [CBasketConfigurationKeys.CheckEveryGrossPriceForManualDiscount]: false,
  [CBasketConfigurationKeys.UserFeedbackEnabled]: false,
  [CBasketConfigurationKeys.UserFeedbackWriteEffectEnabled]: false,
  [CBasketConfigurationKeys.DisableEditBasketOnReview]: false,
  [CBasketConfigurationKeys.PendingBasketsAlert]: '',
  [CBasketConfigurationKeys.DropShipConfirmText]: false,
};
